<template lang="pug">
Card.address-search.selections-card
  template(#header v-if="!hideTitle")
    label.title(for="autoaddress-input") {{ $t('selections.forms.address') }}
  template(#content)
    .aa-control(id="aa-control", ref="aaRef")
</template>
<script lang="ts">
import { useSettings } from "@/store/settings";
import { Options, PostLookupResult } from "@/types";
import { defineComponent } from "vue";
import { storeToRefs } from "pinia";
import { onMounted, onUnmounted, ref } from "vue";
import Card from "primevue/card";
import { useSelections } from "@/store/selections";

declare function Autoaddress(options: Options): void;
export default defineComponent({
  name: "AddressSearch",
  components: { Card },
  props: {
    hideTitle: {
      type: Boolean,
      required: false,
    },
  },
  setup() {
    const { setAddressResult } = useSelections();
    const settingsStore = useSettings();
    const { settings } = storeToRefs(settingsStore);
    const aaRef = ref<HTMLDivElement>();
    let address = "";

    onMounted(async () => {
      if (!aaRef.value) return;

      // Init autoaddress
      Autoaddress({
        apiKey: settings.value?.autoaddressKey || "",
        elementId: "aa-control",
        integrationType: "CustomerForm",
        onPostLookup: (result: PostLookupResult) => setAddress(result),
        onError: (e) => {
          if (e.errorCode === 401) {
            settingsStore.clearAutoaddress();
          }
        },
      });

      const aaInput = aaRef.value.querySelector<HTMLInputElement>('#autoaddress-input');
      if (aaInput) {
        aaInput.onfocus = () => { aaInput.value = ""; };
        aaInput.onblur = () => { aaInput.value = address; };
      }
      fixImgAccessibility(aaRef.value);

      const mo = new MutationObserver(() => fixImgAccessibility(aaRef.value));
      mo.observe(aaRef.value, { childList: true, subtree: true });
      onUnmounted(() => mo.disconnect());

    });

    function setAddress(result: PostLookupResult) {
      if (!aaRef.value) return;

      address = [
        ...result.address.lines,
        result.address?.region,
        result.address?.postcode,
      ]
        .filter((x) => !!x && x.value.trim() !== "")
        .map((x) => x.value.toString())
        .join(", ");

      aaRef.value.getElementsByTagName("input")[0].value = address;

      setAddressResult(result);
    }

    function fixImgAccessibility(rootEl: HTMLElement | undefined) {
      if (!rootEl) return
      rootEl.querySelectorAll('#autoaddress-button img, #autoaddress-clear-button img')
          .forEach((img) => {
            img.setAttribute('alt', '');
            img.setAttribute('aria-hidden', 'true');
            img.removeAttribute('role');
          });

      const input = rootEl.querySelector<HTMLInputElement>('#autoaddress-input');
      const label = document.querySelector<HTMLLabelElement>('label[for="autoaddress-input"]');
      if (input && label) {
        if (!label.id) label.id = 'autoaddress-input-label';
        input.setAttribute('aria-labelledby', label.id);
      }
    }

    return { aaRef };
  },
});
</script>
<style scoped lang="scss">
.address-search {
  margin: 1.5rem;
  z-index: 9999999;

  :deep(.p-card-content) {
    .aa-control {
      max-width: 40rem;

      #autoaddress-button,
      #autoaddress-input {
        border-radius: 0;
      }

      #autoaddress-input {
        color: $font-color-default;
        background: $background-color-default;
        border: 1px solid $border-color;
        box-shadow: none;
      }

      #autoaddress-list {
        border-radius: 0;

        li {
          padding: 0.5rem 1.5rem;
        }
      }

      #autoaddress-manual-enter {
        display: none;
      }

      .autoaddress-input-control {
        display: flex !important;
      }
    }
  }
}
</style>
